/**
 * Theme Provider
 *
 * Manages theme state and applies dark/light mode class to HTML element.
 * - Syncs with uiStore for persistence
 * - Detects system preference when theme is 'system'
 * - Adds smooth transition on theme change
 */

import { useEffect } from 'react';
import { useUIStore, selectTheme, selectResolvedTheme } from '@/stores/uiStore';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useUIStore(selectTheme);
  const resolvedTheme = useUIStore(selectResolvedTheme);
  const setResolvedTheme = useUIStore((state) => state.setResolvedTheme);

  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (theme === 'system') {
        setResolvedTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Set initial system preference
    if (theme === 'system') {
      setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
    } else {
      setResolvedTheme(theme);
    }

    // Listen for system preference changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, setResolvedTheme]);

  // Apply theme class to HTML element
  useEffect(() => {
    const root = document.documentElement;

    // Add transition class for smooth theme change
    root.classList.add('theme-transition');

    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Remove transition class after animation completes
    const timeout = setTimeout(() => {
      root.classList.remove('theme-transition');
    }, 300);

    return () => clearTimeout(timeout);
  }, [resolvedTheme]);

  return <>{children}</>;
}
